/**
 * Grant an owner a share of a property.
 *
 * This is an access-control screen wearing a data-entry hat: creating a row here is
 * what lets someone see the property in the owner portal, from `startDate` onward.
 * The copy says that, because "add owner" reads like bookkeeping and is not.
 *
 * Shares are entered as a percentage and sent as basis points. 33.33% is 3333 bp
 * exactly, which is the whole reason the column is an integer — three equal owners
 * sum to 9999 and the remaining basis point is a visible rounding decision rather
 * than a float that silently fails to reach 1.0.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useList, useOne } from '@refinedev/core';
import { api, ApiError } from '../../../lib/api.js';
import { Field, FormErrors, TextInput, fieldErrors } from '../../../components/Form.js';

interface OwnerOption {
  id: string;
  displayName: string;
  email: string;
}

interface OwnershipRow {
  id: string;
  ownerId: string;
  ownerName?: string;
  basisPoints: number;
  percent: string;
  isCurrent: boolean;
}

export function OwnershipForm() {
  const { id: propertyId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ownerId, setOwnerId] = useState('');
  const [percent, setPercent] = useState('100');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const { data: propertyData } = useOne<{ id: string; name: string }>({
    resource: 'properties',
    id: propertyId ?? '',
    queryOptions: { enabled: Boolean(propertyId) },
  });

  const { data: ownersData, isLoading: ownersLoading } = useList<OwnerOption>({
    resource: 'owners',
    pagination: { current: 1, pageSize: 200 },
  });

  const { data: existingData } = useList<OwnershipRow>({
    resource: 'ownerships',
    filters: [{ field: 'propertyId', operator: 'eq', value: propertyId }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(propertyId) },
  });

  const owners = ownersData?.data ?? [];
  const currentlyHeld = (existingData?.data ?? [])
    .filter((row) => row.isCurrent)
    .reduce((sum, row) => sum + row.basisPoints, 0);
  const availableBasisPoints = 10_000 - currentlyHeld;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      // Percentage → basis points. Rounded, because "33.333" typed into a percent
      // box has to land on an integer somewhere and doing it here makes the result
      // visible in the confirmation rather than inside the API.
      const basisPoints = Math.round(Number(percent) * 100);

      await api('/ownerships', {
        method: 'POST',
        body: JSON.stringify({ propertyId, ownerId, basisPoints, startDate }),
      });

      void navigate(`/staff/properties/${propertyId ?? ''}`);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  const errors = fieldErrors(error);

  // The API's own message is authoritative and includes the arithmetic; this is a
  // pre-flight hint so staff are not surprised by a rejection they could have seen.
  const overAllocated = Math.round(Number(percent) * 100) > availableBasisPoints;

  return (
    <section>
      <h1>Add owner{propertyData?.data ? ` to ${propertyData.data.name}` : ''}</h1>

      <p className="alert alert--warn">
        Creating this row grants portal access to the property from the start date. Ending it later
        revokes access; the row is never deleted, because it justifies statements already issued.
      </p>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={error} />

        <Field label="Owner" name="ownerId" required error={errors.ownerId}>
          <select
            id="ownerId"
            name="ownerId"
            className="field__input"
            value={ownerId}
            onChange={(event) => setOwnerId(event.target.value)}
            required
            disabled={ownersLoading}
          >
            <option value="">{ownersLoading ? 'Loading owners…' : 'Select an owner'}</option>
            {owners.map((owner) => (
              <option key={owner.id} value={owner.id}>
                {owner.displayName} · {owner.email}
              </option>
            ))}
          </select>
        </Field>

        {owners.length === 0 && !ownersLoading ? (
          <p className="muted">
            No owners on file yet.{' '}
            <button
              type="button"
              className="button button--quiet"
              onClick={() => void navigate('/staff/owners/new')}
            >
              Create one first
            </button>
          </p>
        ) : null}

        <Field
          label="Share (%)"
          name="basisPoints"
          required
          hint={`${(availableBasisPoints / 100).toFixed(2)}% unallocated for the current period.`}
          error={errors.basisPoints}
        >
          <TextInput
            name="basisPoints"
            type="number"
            min="0.01"
            max="100"
            step="0.01"
            value={percent}
            onChange={setPercent}
            required
          />
        </Field>

        {overAllocated ? (
          <p className="alert alert--warn">
            That is more than the {(availableBasisPoints / 100).toFixed(2)}% still unallocated for
            overlapping periods. The API will refuse it.
          </p>
        ) : null}

        <Field
          label="Start date"
          name="startDate"
          required
          hint="Access begins on this date."
          error={errors.startDate}
        >
          <TextInput
            name="startDate"
            type="date"
            value={startDate}
            onChange={setStartDate}
            required
          />
        </Field>

        <div className="row">
          <button type="submit" className="button" disabled={busy || !ownerId}>
            {busy ? 'Saving…' : 'Grant ownership'}
          </button>
          <button
            type="button"
            className="button button--quiet"
            onClick={() => void navigate(`/staff/properties/${propertyId ?? ''}`)}
          >
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}

/** Re-exported so callers can narrow on it without importing from lib/api. */
export { ApiError };
